import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Clock,
  Settings,
  XCircle,
  ArrowRight,
  Pencil,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const STATUS_CONFIG = {
  implemented: {
    label: "Implemented",
    labelUa: "Впроваджені",
    color: "bg-green-500/10 text-green-400 border-green-500/20",
    icon: CheckCircle2,
    iconColor: "text-green-500",
  },
  in_progress: {
    label: "In Progress",
    labelUa: "В процесі впровадження",
    color: "bg-sky-500/10 text-sky-400 border-sky-500/20",
    icon: Clock,
    iconColor: "text-sky-500",
  },
  additional_dev: {
    label: "Additional Dev",
    labelUa: "Додаткова розробка",
    color: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    icon: Settings,
    iconColor: "text-orange-500",
  },
  disconnected: {
    label: "Disconnected",
    labelUa: "Відключені",
    color: "bg-red-500/10 text-red-400 border-red-500/20",
    icon: XCircle,
    iconColor: "text-red-500",
  },
};

const CrmDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("implemented");
  const [expandedClient, setExpandedClient] = useState(null);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await crmApi.getDashboard();
      setData(response.data);
    } catch (error) {
      console.error("Error fetching CRM dashboard:", error);
      toast.error("Failed to load CRM dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient({
      ...client,
      implementation_status: client.implementation_status || "implemented",
      disconnection_reason: client.disconnection_reason || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveClient = async () => {
    if (!editingClient) return;
    
    setSaving(true);
    try {
      await crmApi.updateClient(editingClient.id, {
        implementation_status: editingClient.implementation_status,
        disconnection_reason: editingClient.disconnection_reason,
        users_count: editingClient.users_count,
      });
      toast.success("Client updated successfully");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  const renderUsersChange = (change) => {
    if (!change || change === 0) return null;
    
    return (
      <div className={cn(
        "flex items-center gap-1 text-xs font-medium",
        change > 0 ? "text-green-400" : "text-red-400"
      )}>
        {change > 0 ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        <span>{change > 0 ? `+${change}` : change}</span>
      </div>
    );
  };

  const renderClientRow = (client, showDisconnectionReason = false) => {
    const isExpanded = expandedClient === client.id;
    
    return (
      <React.Fragment key={client.id}>
        <TableRow 
          className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
          onClick={() => setExpandedClient(isExpanded ? null : client.id)}
        >
          <TableCell className="py-3">
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-zinc-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-zinc-500" />
              )}
              <div>
                <p className="text-zinc-50 font-medium">{client.name}</p>
                {client.edrpou && (
                  <p className="text-xs text-zinc-500">ЄДРПОУ: {client.edrpou}</p>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell className="py-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <span className="text-zinc-50 font-mono">{client.users_count || 0}</span>
              {renderUsersChange(client.users_change)}
            </div>
          </TableCell>
          <TableCell className="py-3 text-center">
            <Badge variant="outline" className={cn(
              "text-xs",
              client.deployment_type === "cloud" 
                ? "bg-sky-500/10 text-sky-400 border-sky-500/20"
                : "bg-purple-500/10 text-purple-400 border-purple-500/20"
            )}>
              {client.deployment_type === "cloud" ? "Cloud" : "Box"}
            </Badge>
          </TableCell>
          <TableCell className="py-3 text-center">
            <span className="text-zinc-400 text-sm">${client.price_per_user || 50}/user</span>
          </TableCell>
          {showDisconnectionReason && (
            <TableCell className="py-3">
              <span className="text-red-400 text-sm">{client.disconnection_reason || "-"}</span>
            </TableCell>
          )}
          <TableCell className="py-3 text-right">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-zinc-400 hover:text-zinc-50"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClient(client);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </TableCell>
        </TableRow>
        {isExpanded && (
          <TableRow className="border-zinc-800 bg-zinc-900/50">
            <TableCell colSpan={showDisconnectionReason ? 6 : 5} className="py-4">
              <div className="pl-8 space-y-2 text-sm">
                {client.full_name && (
                  <div>
                    <span className="text-zinc-500">Full details:</span>
                    <pre className="text-zinc-300 mt-1 whitespace-pre-wrap text-xs bg-zinc-800/50 p-2 rounded">
                      {client.full_name}
                    </pre>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-4 mt-2">
                  <div>
                    <span className="text-zinc-500">GPT:</span>
                    <span className="text-zinc-50 ml-2">{client.has_gpt ? "Yes" : "No"}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Support:</span>
                    <span className="text-zinc-50 ml-2 capitalize">{client.support_type || "none"}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Support Price:</span>
                    <span className="text-zinc-50 ml-2">${client.support_price || 0}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Created:</span>
                    <span className="text-zinc-50 ml-2">
                      {client.created_at ? new Date(client.created_at).toLocaleDateString() : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const byStatus = data?.by_status || {};

  return (
    <div data-testid="crm-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">CRM Clients Dashboard</h1>
          <p className="text-sm text-zinc-500">Overview of all clients by implementation status</p>
        </div>
        <Link to="/income/crm">
          <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            Payments & Income <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Total Clients</span>
              <Building2 className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50">{summary.total_clients || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {summary.active_clients || 0} active
            </p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Total Users</span>
              <Users className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-400">{summary.total_users || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">Across active clients</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Users Added (30d)</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">+{summary.users_added_30d || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">New users this month</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Users Removed (30d)</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-400">-{summary.users_removed_30d || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              Net: {(summary.net_users_change || 0) >= 0 ? "+" : ""}{summary.net_users_change || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const statusData = byStatus[status] || {};
          const Icon = config.icon;
          
          return (
            <Card 
              key={status}
              className={cn(
                "border-zinc-800 cursor-pointer transition-all",
                activeTab === status ? "bg-zinc-800/70 ring-1 ring-zinc-600" : "bg-zinc-900/50 hover:bg-zinc-800/50"
              )}
              onClick={() => setActiveTab(status)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className={cn("h-5 w-5", config.iconColor)} />
                  <Badge variant="outline" className={cn("text-xs", config.color)}>
                    {statusData.count || 0}
                  </Badge>
                </div>
                <p className="text-sm font-medium text-zinc-50">{config.labelUa}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Users className="h-3 w-3 text-zinc-500" />
                  <span className="text-xs text-zinc-400">{statusData.total_users || 0} users</span>
                </div>
                {status !== "disconnected" && (statusData.users_added > 0 || statusData.users_removed > 0) && (
                  <div className="flex items-center gap-3 mt-1">
                    {statusData.users_added > 0 && (
                      <span className="text-xs text-green-400">+{statusData.users_added}</span>
                    )}
                    {statusData.users_removed > 0 && (
                      <span className="text-xs text-red-400">-{statusData.users_removed}</span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Clients Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
            {(() => {
              const IconComponent = STATUS_CONFIG[activeTab]?.icon;
              return IconComponent ? (
                <IconComponent className={cn("h-4 w-4", STATUS_CONFIG[activeTab]?.iconColor)} />
              ) : null;
            })()}
            {STATUS_CONFIG[activeTab]?.labelUa || "Clients"}
            <Badge variant="outline" className="ml-2 text-xs bg-zinc-800">
              {byStatus[activeTab]?.count || 0} companies
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {byStatus[activeTab]?.clients?.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No clients in this category</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs font-medium">Company</TableHead>
                  <TableHead className="text-zinc-500 text-xs font-medium text-center">Users</TableHead>
                  <TableHead className="text-zinc-500 text-xs font-medium text-center">Type</TableHead>
                  <TableHead className="text-zinc-500 text-xs font-medium text-center">Price</TableHead>
                  {activeTab === "disconnected" && (
                    <TableHead className="text-zinc-500 text-xs font-medium">Reason</TableHead>
                  )}
                  <TableHead className="text-zinc-500 text-xs font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byStatus[activeTab]?.clients?.map((client) => 
                  renderClientRow(client, activeTab === "disconnected")
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Edit Client Status</DialogTitle>
          </DialogHeader>
          
          {editingClient && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400 text-sm">Company</Label>
                <p className="text-zinc-50 font-medium">{editingClient.name}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Users Count</Label>
                <Input
                  type="number"
                  min={0}
                  value={editingClient.users_count || 0}
                  onChange={(e) => setEditingClient({
                    ...editingClient,
                    users_count: parseInt(e.target.value) || 0
                  })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Implementation Status</Label>
                <Select
                  value={editingClient.implementation_status}
                  onValueChange={(value) => setEditingClient({
                    ...editingClient,
                    implementation_status: value
                  })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="implemented">Впроваджені</SelectItem>
                    <SelectItem value="in_progress">В процесі впровадження</SelectItem>
                    <SelectItem value="additional_dev">Додаткова розробка</SelectItem>
                    <SelectItem value="disconnected">Відключені</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {editingClient.implementation_status === "disconnected" && (
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">Disconnection Reason</Label>
                  <Textarea
                    value={editingClient.disconnection_reason || ""}
                    onChange={(e) => setEditingClient({
                      ...editingClient,
                      disconnection_reason: e.target.value
                    })}
                    placeholder="Enter reason for disconnection..."
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[80px]"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClient}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmDashboardPage;
